import React, { useState, useEffect, useCallback, useRef } from 'react';
import CreditsBadge from '../components/CreditsBadge';
import { useRefreshable } from '../context/RefreshContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import FloorPlanImport from '../components/FloorPlanImport/FloorPlanImport';
import { floorPlanToDesign } from '../utils/floorPlanToRooms';
import {
  createCollection, getUserCollections, deleteCollection,
  createProject, getUserProjects, getProjectsByCollection, deleteProject,
  renameProject, duplicateProject, saveProjectToWeb,
} from '../firebase/workspaceService';
import { GRID_SIZES, DEFAULT_GRID_SIZE_CODE } from '../store/houseSlice';
import UserMenu from '../components/UserMenu';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import ShareButton from '../components/ShareButton';
import CostCalculator from '../components/CostCalculator';
import PublishDesignModal from '../components/PublishDesignModal';
import RealStagingModal from '../components/RealStaging/RealStagingModal';
import { USER_TYPES } from '../constants/designer';
import { currencySymbol, recalcProject } from '../utils/costEstimate';
import { setProjectPrice } from '../firebase/workspaceService';
import './Workspace.css';
import BackBar from '../components/BackBar';

const SIZE_OPTIONS = ['S', 'M', 'L'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

// ── SVG placeholder for project cards ────────────────────────────────────────

function CardPlaceholder({ seed = 0 }) {
  const palettes = [
    ['#EEF0FB', '#C7CAEF', '#6366F1'],
    ['#EDF6FF', '#C2D9F5', '#3B82F6'],
    ['#ECFDF5', '#BBF7D0', '#10B981'],
    ['#FFF7ED', '#FED7AA', '#F97316'],
    ['#F5F3FF', '#DDD6FE', '#7C3AED'],
  ];
  const [bg, mid, acc] = palettes[seed % palettes.length];
  return (
    <svg viewBox="0 0 300 160" xmlns="http://www.w3.org/2000/svg" className="ws-card-thumb-svg">
      <rect width="300" height="160" fill={bg} />
      <rect x="20" y="20" width="120" height="80" rx="6" fill={mid} />
      <rect x="160" y="20" width="120" height="36" rx="6" fill={mid} />
      <rect x="160" y="64" width="80" height="36" rx="6" fill={mid} />
      <rect x="20" y="112" width="260" height="28" rx="6" fill={mid} />
      <circle cx="258" cy="56" r="18" fill={acc} opacity="0.55" />
      <rect x="30" y="30" width="40" height="6" rx="3" fill={acc} opacity="0.6" />
      <rect x="30" y="42" width="60" height="4" rx="2" fill={acc} opacity="0.4" />
      <rect x="30" y="52" width="50" height="4" rx="2" fill={acc} opacity="0.4" />
    </svg>
  );
}

function CollectionPlaceholder({ seed = 0 }) {
  const palettes = [
    ['#EEF0FB', '#6366F1'],
    ['#EDF6FF', '#3B82F6'],
    ['#ECFDF5', '#10B981'],
    ['#FFF7ED', '#F97316'],
  ];
  const [bg, acc] = palettes[seed % palettes.length];
  return (
    <svg viewBox="0 0 300 140" xmlns="http://www.w3.org/2000/svg" className="ws-col-thumb-svg">
      <rect width="300" height="140" fill={bg} />
      {[0, 1, 2].map(i => (
        <rect key={i} x={24 + i * 90} y="20" width="72" height="100" rx="8" fill={acc} opacity={0.35 - i * 0.08} />
      ))}
      <rect x="24" y="20" width="72" height="100" rx="8" fill={acc} opacity="0.7" />
    </svg>
  );
}

// ── Error banner ─────────────────────────────────────────────────────────────

function ErrorBanner({ error, onRetry }) {
  const isIndex = error?.type === 'index';
  return (
    <div className="ws-error-banner">
      <div className="ws-error-icon">
        {isIndex ? (
          <svg viewBox="0 0 24 24" fill="none" width="22" height="22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" width="22" height="22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><circle cx="12" cy="17" r="0.5" fill="currentColor"/>
          </svg>
        )}
      </div>
      <div className="ws-error-body">
        <p className="ws-error-title">{isIndex ? 'Database index is building…' : 'Failed to load workspace'}</p>
        <p className="ws-error-msg">{error.message}</p>
        {isIndex && (
          <p className="ws-error-hint">
            Go to your{' '}
            <a href="https://console.firebase.google.com/project/tunify-40a72/firestore/indexes" target="_blank" rel="noreferrer" className="ws-error-link">
              Firebase Console → Indexes
            </a>{' '}
            and confirm the composite index for <code>projects</code> is active.
          </p>
        )}
      </div>
      <button className="ws-btn ws-btn--outline ws-error-retry" onClick={onRetry}>
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
        </svg>
        Retry
      </button>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

function Modal({ title, label, placeholder, onConfirm, onClose, busy, showSizePicker, initialValue = '', confirmLabel, busyLabel }) {
  const { t } = useT();
  const [value, setValue] = useState(initialValue);
  const [size,  setSize]  = useState(DEFAULT_GRID_SIZE_CODE);
  const [err,   setErr]   = useState('');
  // Optional 2D floor plan, detected but not yet converted — see FloorPlanImport.
  const [plan,  setPlan]  = useState(null);

  function handleSubmit(e) {
    e.preventDefault();
    if (!value.trim()) { setErr(t('ws_field_required')); return; }
    onConfirm(value.trim(), size, plan);
  }

  return (
    <div className="ws-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ws-modal" role="dialog" aria-modal="true">
        <div className="ws-modal-header">
          <h3 className="ws-modal-title">{title}</h3>
          <button className="ws-modal-close" onClick={onClose} disabled={busy} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="ws-modal-body">
            <label className="ws-modal-label">{label}</label>
            <input
              className={`ws-modal-input${err ? ' ws-modal-input--err' : ''}`}
              placeholder={placeholder}
              value={value}
              onChange={e => { setValue(e.target.value); setErr(''); }}
              autoFocus
              disabled={busy}
            />
            {err && <p className="ws-modal-err">{err}</p>}

            {showSizePicker && (
              <>
                <label className="ws-modal-label" style={{ marginTop: 14 }}>{t('ws_size_label')}</label>
                <div className="ws-size-row">
                  {SIZE_OPTIONS.map(code => (
                    <button
                      key={code}
                      type="button"
                      className={`ws-size-btn${size === code ? ' ws-size-btn--active' : ''}`}
                      onClick={() => setSize(code)}
                      disabled={busy}
                    >
                      <span className="ws-size-code">{code}</span>
                      <span className="ws-size-dims">{GRID_SIZES[code]}×{GRID_SIZES[code]}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Offered only where a project is actually being created — a
                collection has no floor plan. */}
            {showSizePicker && <FloorPlanImport onChange={setPlan} disabled={busy} />}
          </div>
          <div className="ws-modal-footer">
            <button type="button" className="ws-btn ws-btn--ghost" onClick={onClose} disabled={busy}>{t('cancel')}</button>
            <button type="submit" className="ws-btn ws-btn--primary" disabled={busy || !value.trim()}>
              {busy ? <span className="ws-spinner" /> : null}
              {busy ? (busyLabel || t('ws_creating')) : (confirmLabel || t('ws_create'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Confirm (yes/no) modal ─────────────────────────────────────────────────────

function ConfirmModal({ title, message, itemName, confirmLabel, onConfirm, onCancel, busy }) {
  const { t } = useT();
  return (
    <div className="ws-overlay" onClick={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="ws-modal ws-modal--confirm" role="dialog" aria-modal="true">
        <div className="ws-modal-header">
          <h3 className="ws-modal-title">{title}</h3>
          <button className="ws-modal-close" onClick={onCancel} disabled={busy} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="ws-modal-body">
          <p className="ws-confirm-msg">{message}</p>
          {itemName && <p className="ws-confirm-name">“{itemName}”</p>}
        </div>
        <div className="ws-modal-footer">
          <button type="button" className="ws-btn ws-btn--ghost" onClick={onCancel} disabled={busy}>{t('ws_no')}</button>
          <button type="button" className="ws-btn ws-btn--danger" onClick={onConfirm} disabled={busy}>
            {busy ? <span className="ws-spinner" /> : null}
            {confirmLabel || t('ws_yes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Three-dot menu ────────────────────────────────────────────────────────────

// Menu-item icons, keyed by action.
const DOT_ICONS = {
  rename: (
    <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z"/>
    </svg>
  ),
  share: (
    <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/>
    </svg>
  ),
  copy: (
    <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
    </svg>
  ),
  delete: (
    <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
    </svg>
  ),
};

// Generic 3-dot menu. `actions` = [{ key, label, danger?, onClick }].
function DotMenu({ actions }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  // Close on any click/tap outside the menu, and on Escape. Uses a document
  // listener (not the old fixed backdrop) because .ws-dot-wrap's stacking
  // context trapped the backdrop below other cards, so outside clicks missed it.
  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div className="ws-dot-wrap" ref={wrapRef}>
      <button
        className="ws-dot-btn"
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        aria-label="More options"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
        </svg>
      </button>
      {open && (
        <>
          <div className="ws-dot-menu">
            {actions.map(a => (
              <button
                key={a.key}
                className={`ws-dot-item${a.danger ? ' ws-dot-item--danger' : ''}`}
                onClick={e => { e.stopPropagation(); setOpen(false); a.onClick(); }}
              >
                {DOT_ICONS[a.key]}
                {a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Project card ──────────────────────────────────────────────────────────────

function ProjectCard({ project, index, collections, onRename, onShare, onCopy, onDelete, onEstimate, onReload, reloading, onClick, onPublish }) {
  const { t } = useT();
  const colName = collections.find(c => c.id === project.collectionId)?.collectionName;
  const hasPrice = project.price != null && project.price !== '';
  const actions = [
    { key: 'rename', label: t('ws_rename'), onClick: () => onRename(project) },
    { key: 'share',  label: t('share'),     onClick: () => onShare(project) },
    { key: 'copy',   label: t('ws_copy'),   onClick: () => onCopy(project) },
    // Designers only — publishing puts the design on the public profile, which
    // a customer account has no concept of.
    ...(onPublish ? [{ key: 'publish', label: t('pub_action'), onClick: () => onPublish(project) }] : []),
    { key: 'delete', label: t('ws_delete'), danger: true, onClick: () => onDelete(project) },
  ];
  return (
    <div className="ws-card" onClick={onClick}>
      <div className="ws-card-thumb">
        <CardPlaceholder seed={index} />
      </div>
      <div className="ws-card-footer">
        <div className="ws-card-meta">
          <div className="ws-card-avatar">{getInitials(project.projectName)}</div>
          <div className="ws-card-info">
            <p className="ws-card-name">{project.projectName}</p>
            {colName && (
              <p className="ws-card-col">
                <svg viewBox="0 0 24 24" fill="none" width="11" height="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                </svg>
                {colName}
              </p>
            )}
          </div>
        </div>
        <div className="ws-card-actions">
          <button
            className={`ws-price-btn${hasPrice ? ' ws-price-btn--set' : ''}`}
            onClick={e => { e.stopPropagation(); onEstimate(project); }}
            title={t('cc_title')}
          >
            {hasPrice ? (
              <span className="ws-price-value">{currencySymbol(project.amount)}{Number(project.price).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="12" y1="10" x2="14" y2="10"/><line x1="16" y1="10" x2="16" y2="18"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="12" y1="14" x2="14" y2="14"/><line x1="8" y1="18" x2="14" y2="18"/>
                </svg>
                {t('cc_estimate')}
              </>
            )}
          </button>
          {hasPrice && (
            <button
              className={`ws-reload-btn${reloading ? ' ws-reload-btn--busy' : ''}`}
              onClick={e => { e.stopPropagation(); if (!reloading) onReload(project); }}
              disabled={reloading}
              title={t('cc_reload')}
            >
              <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
              </svg>
            </button>
          )}
          <DotMenu actions={actions} />
        </div>
      </div>
    </div>
  );
}

// ── Collection card ───────────────────────────────────────────────────────────

function CollectionCard({ col, index, projectCount, onDelete, onClick }) {
  const { t } = useT();
  const projWord = projectCount !== 1 ? t('ws_project_many') : t('ws_project_one');
  return (
    <div className="ws-card" onClick={onClick}>
      <div className="ws-card-thumb ws-card-thumb--col">
        <CollectionPlaceholder seed={index} />
        <div className="ws-col-badge">{projectCount} {projWord}</div>
      </div>
      <div className="ws-card-footer">
        <div className="ws-card-meta">
          <div className="ws-card-avatar ws-card-avatar--col">
            <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
            </svg>
          </div>
          <div className="ws-card-info">
            <p className="ws-card-name">{col.collectionName}</p>
            <p className="ws-card-col">{t('ws_collection_label')}</p>
          </div>
        </div>
        <DotMenu actions={[{ key: 'delete', label: t('ws_delete'), danger: true, onClick: () => onDelete(col) }]} />
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ icon, title, subtitle, action, onAction }) {
  return (
    <div className="ws-empty">
      <div className="ws-empty-icon">{icon}</div>
      <h3 className="ws-empty-title">{title}</h3>
      <p className="ws-empty-sub">{subtitle}</p>
      {action && (
        <button className="ws-btn ws-btn--primary" onClick={onAction}>{action}</button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

// `embedded` drops the page shell — its own nav, its own Back bar, its own
// root element — and renders the workspace BODY alone, so another page can host
// it. That is how a customer's profile shows their projects in place instead of
// sending them to a second page for the only thing they own here.
//
// A prop rather than a copy: this file is nine hundred lines of project and
// collection management, modals included, and two of them would drift apart the
// first time either was touched. Everything below the shell is untouched, so
// the standalone /workspace route behaves exactly as before.
export default function Workspace({ embedded = false }) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const isDesigner = profile?.userType === USER_TYPES.DESIGNER;
  const { t } = useT();

  const [tab,              setTab]              = useState('projects'); // 'projects' | 'collections'
  const [activeCollection, setActiveCollection] = useState(null);      // null | col object
  const [projects,         setProjects]         = useState([]);
  const [collections,      setCollections]      = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [loadError,        setLoadError]        = useState(null);

  // Modals
  const [modal,     setModal]     = useState(null); // null | 'project' | 'collection' | 'addToCol'
  const [modalBusy, setModalBusy] = useState(false);
  // Real-room AI staging — independent of the project/collection modals above,
  // so it keeps its own flag rather than joining their union type.
  const [stagingOpen, setStagingOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null); // project being renamed
  const [shareTarget,  setShareTarget]  = useState(null); // project being shared
  const [publishTarget, setPublishTarget] = useState(null); // project being published to the profile
  const [confirmDelete, setConfirmDelete] = useState(null); // { kind, id, name } awaiting yes/no
  const [confirmBusy,  setConfirmBusy]  = useState(false);
  const [costTarget,   setCostTarget]   = useState(null); // project whose cost estimator is open
  const [reloadingId,  setReloadingId]  = useState(null); // project id being re-estimated

  // ── Load data ──────────────────────────────────────────────────────────────

  // `quiet` skips the loading state so a pull-to-refresh replaces the grid in
  // place rather than blanking it to a spinner — the pull already has its own
  // indicator, and two at once reads as the page reloading.
  //
  // Destructured with a default so `onRetry={loadAll}` still works: React hands
  // that a click event, which simply yields `quiet === undefined`.
  const loadAll = useCallback(async ({ quiet = false } = {}) => {
    if (!user) return;
    if (!quiet) setLoading(true);
    setLoadError(null);
    // Load collections and projects INDEPENDENTLY — a failure on one (e.g. a
    // missing Firestore rule for `collections`) must never hide the other. Only
    // show the error banner if projects — the main content — actually failed.
    const [colsRes, projsRes] = await Promise.allSettled([
      getUserCollections(user.uid),
      getUserProjects(user.uid),
    ]);

    if (colsRes.status === 'fulfilled') setCollections(colsRes.value);
    else { console.error('Failed to load collections:', colsRes.reason); setCollections([]); }

    if (projsRes.status === 'fulfilled') {
      setProjects(projsRes.value);
    } else {
      const err = projsRes.reason;
      console.error('Failed to load projects:', err);
      const isIndexError = err?.code === 'failed-precondition' || err?.message?.includes('index');
      setLoadError(
        isIndexError
          ? { type: 'index', message: 'A required database index is still building. This takes ~2 minutes on first run. Refresh the page to try again.' }
          : { type: 'generic', message: 'Failed to load your workspace. Check your connection and try again.' }
      );
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useRefreshable(() => loadAll({ quiet: true }));

  // ── Modal handlers ─────────────────────────────────────────────────────────

  // Converts an uploaded plan into the saved-design document and stores it, so
  // the project opens in the Builder with real, editable rooms. Failure here is
  // never fatal: the project has already been created and simply starts empty.
  async function applyPlanToProject(id, plan, size) {
    if (!plan?.detection) return;
    try {
      const built = floorPlanToDesign(plan.detection, {
        scaleFactor: plan.scaleFactor,
        gridSize: GRID_SIZES[size],
        imageWalls: plan.imageWalls,
      });
      if (built) await saveProjectToWeb(id, JSON.stringify(built.design));
    } catch (err) {
      console.error('Floor plan import failed:', err);
    }
  }

  async function handleCreateProject(name, size, plan) {
    setModalBusy(true);
    try {
      const id = await createProject(user.uid, name, null, size);
      await applyPlanToProject(id, plan, size);
      setProjects(p => [{ id, userId: user.uid, collectionId: null, projectName: name, size, data: '{}', addDateTime: null }, ...p]);
      setModal(null);
    } catch (err) {
      console.error(err);
    } finally {
      setModalBusy(false);
    }
  }

  async function handleCreateCollection(name) {
    setModalBusy(true);
    try {
      const id = await createCollection(user.uid, name);
      setCollections(c => [{ id, userId: user.uid, collectionName: name, addDateTime: null }, ...c]);
      setModal(null);
    } catch (err) {
      console.error(err);
    } finally {
      setModalBusy(false);
    }
  }

  async function handleAddToCollection(name, size) {
    if (!activeCollection) return;
    setModalBusy(true);
    try {
      const id = await createProject(user.uid, name, activeCollection.id, size);
      await applyPlanToProject(id, plan, size);
      setProjects(p => [{ id, userId: user.uid, collectionId: activeCollection.id, projectName: name, size, data: '{}', addDateTime: null }, ...p]);
      setModal(null);
    } catch (err) {
      console.error(err);
    } finally {
      setModalBusy(false);
    }
  }

  async function handleDeleteProject(id) {
    try {
      await deleteProject(id);
      setProjects(p => p.filter(x => x.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  async function handleConfirmRename(name) {
    if (!renameTarget) return;
    const id = renameTarget.id;
    setModalBusy(true);
    try {
      await renameProject(id, name);
      setProjects(p => p.map(x => x.id === id ? { ...x, projectName: name } : x));
      setRenameTarget(null);
    } catch (err) {
      console.error(err);
    } finally {
      setModalBusy(false);
    }
  }

  async function handleCopyProject(project) {
    try {
      const newId = await duplicateProject(user.uid, project);
      // Prepend the copy so it's immediately visible at the top of the grid.
      setProjects(p => [{
        ...project,
        id: newId,
        projectName: `${project.projectName || 'Untitled'} copy`,
        addDateTime: null,
      }, ...p]);
    } catch (err) {
      console.error(err);
    }
  }

  // Cost estimate saved → reflect the new price/currency on the card and keep
  // the breakdown in memory so reopening the estimator restores the inputs.
  function handleCostSaved(id, price, amount, priceData) {
    setProjects(p => p.map(x => x.id === id ? { ...x, price, amount, priceData } : x));
    setCostTarget(t => (t && t.id === id ? { ...t, price, amount, priceData } : t));
  }

  // Reload = re-apply the saved rates to the project's CURRENT scene (picks up
  // rooms/items added or removed) and save the fresh total. With no saved
  // breakdown to reuse, fall back to opening the estimator.
  async function handleReloadPrice(project) {
    const res = recalcProject(project);
    if (!res) { setCostTarget(project); return; }
    setReloadingId(project.id);
    try {
      await setProjectPrice(project.id, res.grand, res.currency, res.priceData);
      setProjects(p => p.map(x => x.id === project.id
        ? { ...x, price: res.grand, amount: res.currency, priceData: res.priceData } : x));
    } catch (err) {
      console.error('[Cost] reload failed:', err);
    } finally {
      setReloadingId(null);
    }
  }

  async function handleDeleteCollection(id) {
    try {
      await deleteCollection(id);
      setCollections(c => c.filter(x => x.id !== id));
      if (activeCollection?.id === id) setActiveCollection(null);
    } catch (err) {
      console.error(err);
    }
  }

  // Delete now goes through a yes/no confirm; the card handlers just open it.
  const requestDeleteProject    = (project) => setConfirmDelete({ kind: 'project',    id: project.id, name: project.projectName });
  const requestDeleteCollection = (col)     => setConfirmDelete({ kind: 'collection', id: col.id,     name: col.collectionName });

  async function handleConfirmDelete() {
    if (!confirmDelete) return;
    setConfirmBusy(true);
    try {
      if (confirmDelete.kind === 'project') await handleDeleteProject(confirmDelete.id);
      else await handleDeleteCollection(confirmDelete.id);
      setConfirmDelete(null);
    } finally {
      setConfirmBusy(false);
    }
  }

  function openProject(project) {
    navigate(`/app/${project.id}`);
  }

  // ── Derived data ───────────────────────────────────────────────────────────

  const colProjects   = activeCollection ? projects.filter(p => p.collectionId === activeCollection.id) : [];
  const standaloneProjects = projects.filter(p => !p.collectionId);

  const projectCount = (colId) => projects.filter(p => p.collectionId === colId).length;

  // ── Modal config ───────────────────────────────────────────────────────────

  const modalConfig = {
    project:    { title: t('ws_modal_project_title'),    label: t('ws_modal_project_label'),    placeholder: t('ws_modal_project_ph'),    onConfirm: handleCreateProject,     showSizePicker: true },
    collection: { title: t('ws_modal_collection_title'), label: t('ws_modal_collection_label'), placeholder: t('ws_modal_collection_ph'), onConfirm: handleCreateCollection },
    addToCol:   { title: t('ws_modal_addtocol_title'),   label: t('ws_modal_addtocol_label'),   placeholder: t('ws_modal_addtocol_ph'),   onConfirm: handleAddToCollection,   showSizePicker: true },
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Embedded, the host page owns the scroll container and the chrome; a second
  // `.ws-root` inside it would nest one full-height scroller in another.
  const Shell = embedded ? React.Fragment : 'div';
  const shellProps = embedded ? {} : { className: 'ws-root' };
  // ...and a <main> inside the host's own <main> is not valid HTML — a page
  // has one. Embedded this is a section of that page, which is what it is.
  const Body = embedded ? 'section' : 'main';

  return (
    <Shell {...shellProps}>

      {/* ── NAV ── */}
      {!embedded && (
        <header className="ws-nav">
          <button className="ws-nav-brand" onClick={() => navigate('/dashboard')}>
            <img src="/images/Tunify Original logo tab.png" alt="Tunify" className="ws-nav-logo" />
            <span className="ws-nav-name">Tunify</span>
          </button>
          <div className="ws-nav-right">
            <HeaderSearch />
            <CreditsBadge />
          <MessagesButton />
            <NotificationBell />
            <UserMenu />
          </div>
        </header>
      )}

      {/* ── MAIN ── */}
      {!embedded && <BackBar width={1360} />}

      <Body className={`ws-main${embedded ? ' ws-main--embedded' : ''}`}>

        {/* ── Inside-collection view ── */}
        {activeCollection ? (
          <>
            <div className="ws-page-head">
              <div>
                <button className="ws-back-btn" onClick={() => { setActiveCollection(null); setTab('collections'); }}>
                  <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 5l-7 7 7 7"/>
                  </svg>
                  {t('ws_back_collections')}
                </button>
                <h1 className="ws-page-title">{activeCollection.collectionName}</h1>
                <p className="ws-page-sub">{colProjects.length} {colProjects.length !== 1 ? t('ws_project_many') : t('ws_project_one')} {t('ws_in_collection')}</p>
              </div>
              <div className="ws-head-actions">
                <button className="ws-btn ws-btn--primary" onClick={() => setModal('addToCol')}>
                  <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  {t('ws_add_project')}
                </button>
              </div>
            </div>

            {loading ? (
              <div className="ws-loader"><span className="ws-spinner ws-spinner--lg" /></div>
            ) : loadError ? (
              <ErrorBanner error={loadError} onRetry={loadAll} />
            ) : colProjects.length === 0 ? (
              <EmptyState
                icon={<svg viewBox="0 0 24 24" fill="none" width="40" height="40" stroke="#6366F1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="3" x2="12" y2="21"/></svg>}
                title={t('ws_empty_col_title')}
                subtitle={t('ws_empty_col_sub')}
                action={t('ws_add_project_action')}
                onAction={() => setModal('addToCol')}
              />
            ) : (
              <div className="ws-grid">
                {colProjects.map((p, i) => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    index={i}
                    collections={collections}
                    onRename={setRenameTarget}
                    onShare={setShareTarget}
                    onCopy={handleCopyProject}
                    onEstimate={setCostTarget}
                    onReload={handleReloadPrice}
                    reloading={reloadingId === p.id}
                    onDelete={requestDeleteProject}
                    onPublish={isDesigner ? setPublishTarget : null}
                    onClick={() => openProject(p)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* ── Top header ── */}
            <div className="ws-page-head">
              <div>
                <h1 className="ws-page-title">{t('ws_title')}</h1>
                <p className="ws-page-sub">{t('ws_sub')}</p>
              </div>
              <div className="ws-head-actions">
                {/* Stage a photo of a REAL room — unrelated to the projects and
                    collections beside it, which are all 3D designs, so it gets
                    its own icon rather than another "+". */}
                <button className="ws-btn ws-btn--outline ws-btn--ai" onClick={() => setStagingOpen(true)}>
                  <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  {t('rs_open')}
                </button>
                <button className="ws-btn ws-btn--outline" onClick={() => setModal('collection')}>
                  <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  {t('ws_new_collection')}
                </button>
                <button className="ws-btn ws-btn--primary" onClick={() => setModal('project')}>
                  <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  {t('ws_new_project')}
                </button>
              </div>
            </div>

            {/* ── Tabs ── */}
            <div className="ws-tabs">
              <button
                className={`ws-tab${tab === 'projects' ? ' ws-tab--active' : ''}`}
                onClick={() => setTab('projects')}
              >
                {t('ws_tab_projects')}
                <span className="ws-tab-count">{projects.length}</span>
              </button>
              <button
                className={`ws-tab${tab === 'collections' ? ' ws-tab--active' : ''}`}
                onClick={() => setTab('collections')}
              >
                {t('ws_tab_collections')}
                <span className="ws-tab-count">{collections.length}</span>
              </button>
            </div>

            {/* ── Content ── */}
            {loading ? (
              <div className="ws-loader"><span className="ws-spinner ws-spinner--lg" /></div>
            ) : loadError ? (
              <ErrorBanner error={loadError} onRetry={loadAll} />
            ) : tab === 'projects' ? (
              standaloneProjects.length === 0 && projects.length === 0 ? (
                <EmptyState
                  icon={<svg viewBox="0 0 24 24" fill="none" width="40" height="40" stroke="#6366F1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="3" x2="12" y2="21"/></svg>}
                  title={t('ws_empty_projects_title')}
                  subtitle={t('ws_empty_projects_sub')}
                  action={t('ws_new_project_action')}
                  onAction={() => setModal('project')}
                />
              ) : (
                <div className="ws-grid">
                  {projects.map((p, i) => (
                    <ProjectCard
                      key={p.id}
                      project={p}
                      index={i}
                      collections={collections}
                      onRename={setRenameTarget}
                      onShare={setShareTarget}
                      onCopy={handleCopyProject}
                      onEstimate={setCostTarget}
                      onReload={handleReloadPrice}
                      reloading={reloadingId === p.id}
                      onDelete={requestDeleteProject}
                      onPublish={isDesigner ? setPublishTarget : null}
                      onClick={() => openProject(p)}
                    />
                  ))}
                </div>
              )
            ) : (
              collections.length === 0 ? (
                <EmptyState
                  icon={<svg viewBox="0 0 24 24" fill="none" width="40" height="40" stroke="#6366F1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>}
                  title={t('ws_empty_collections_title')}
                  subtitle={t('ws_empty_collections_sub')}
                  action={t('ws_new_collection_action')}
                  onAction={() => setModal('collection')}
                />
              ) : (
                <div className="ws-grid">
                  {collections.map((col, i) => (
                    <CollectionCard
                      key={col.id}
                      col={col}
                      index={i}
                      projectCount={projectCount(col.id)}
                      onDelete={requestDeleteCollection}
                      onClick={() => setActiveCollection(col)}
                    />
                  ))}
                </div>
              )
            )}
          </>
        )}
      </Body>

      {/* ── Modals ── */}
      {modal && modalConfig[modal] && (
        <Modal
          {...modalConfig[modal]}
          onClose={() => { if (!modalBusy) setModal(null); }}
          busy={modalBusy}
        />
      )}

      {/* Rename project */}
      {renameTarget && (
        <Modal
          title={t('ws_rename_title')}
          label={t('ws_modal_project_label')}
          placeholder={t('ws_modal_project_ph')}
          initialValue={renameTarget.projectName || ''}
          confirmLabel={t('ws_save')}
          busyLabel={t('ws_saving')}
          onConfirm={handleConfirmRename}
          onClose={() => { if (!modalBusy) setRenameTarget(null); }}
          busy={modalBusy}
        />
      )}

      {/* Share project — controlled ShareButton (modal only, no trigger) */}
      {shareTarget && (
        <ShareButton
          projectId={shareTarget.id}
          projectName={shareTarget.projectName || ''}
          user={user}
          open
          showTrigger={false}
          onClose={() => setShareTarget(null)}
        />
      )}

      {/* Publish a design to the public profile (designers only) */}
      {publishTarget && (
        <PublishDesignModal
          project={publishTarget}
          onClose={() => setPublishTarget(null)}
        />
      )}

      {/* Delete confirmation (yes/no) */}
      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.kind === 'collection' ? t('ws_confirm_del_col_title') : t('ws_confirm_del_title')}
          message={confirmDelete.kind === 'collection' ? t('ws_confirm_del_col_msg') : t('ws_confirm_del_msg')}
          itemName={confirmDelete.name}
          confirmLabel={t('ws_delete')}
          onConfirm={handleConfirmDelete}
          onCancel={() => { if (!confirmBusy) setConfirmDelete(null); }}
          busy={confirmBusy}
        />
      )}

      {/* Real-room AI staging */}
      <RealStagingModal open={stagingOpen} onClose={() => setStagingOpen(false)} />

      {/* Cost estimator */}
      {costTarget && (
        <CostCalculator
          project={costTarget}
          onClose={() => setCostTarget(null)}
          onSaved={(price, amount, priceData) => handleCostSaved(costTarget.id, price, amount, priceData)}
        />
      )}
    </Shell>
  );
}
